import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { patchItems } from '@/components/Patches/PatchSearchItems';
import { DATASETS } from '@/components/Constants';

import type { Section as SectionType } from '@/types';
import { userAppContext } from '@/context/Context';
import { fetchData } from '@/modules/search/searchBar/apiCalls';
import { handleSearchItems } from '@/modules/search/searchBar/helpers';

function getItemLabel(item: any): string {
  const patched = patchItems(item);
  switch (patched.dataset) {
    case DATASETS.DISEASES:
      return [patched.name, patched.diseaseUMLSCUI].filter(Boolean).join(' · ') || String(patched.id);
    case DATASETS.GENES:
      return [patched.symbol, patched.ncbiID].filter(Boolean).join(' · ') || String(patched.id);
    case DATASETS.VARIANTS:
      return [patched.strID, patched.geneSymbol].filter(Boolean).join(' · ') || String(patched.id);
    case DATASETS.CHEMICALS:
      return [patched.chemPrefName, patched.chemID].filter(Boolean).join(' · ') || String(patched.id);
    default:
      return String(patched.id ?? '');
  }
}

interface SidebarSearchDropdownProps {
  value: string;
  setValue: (v: string) => void;
  searchItems: any[];
  setSearchItems: (updater: (prev: any[]) => any[]) => void;
  onDropdownToggle?: (open: boolean) => void;
  allowedDataset?: string;
}

export function SidebarSearchDropdown({
  value,
  setValue,
  searchItems,
  setSearchItems,
  onDropdownToggle,
  allowedDataset,
}: SidebarSearchDropdownProps) {
  const navigate = useNavigate();
  const { authorization, isAuthenticated, restrictSources, groups } = userAppContext() ?? {};
  const [data, setData] = useState<any>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelTokenRef = useRef<any>(null);

  const noData =
    !data ||
    (data.diseases?.length === 0 &&
      data.genes?.length === 0 &&
      data.variants?.length === 0 &&
      data.chemicals?.length === 0);
  const normalizeDataset = (raw?: string) => {
    const normalized = String(raw ?? '')
      .trim()
      .toUpperCase();
    if (normalized === DATASETS.DISEASES || normalized === 'DISEASE') return DATASETS.DISEASES;
    if (normalized === DATASETS.GENES || normalized === 'GENE') return DATASETS.GENES;
    if (normalized === DATASETS.VARIANTS || normalized === 'VARIANT') return DATASETS.VARIANTS;
    if (normalized === DATASETS.CHEMICALS || normalized === 'CHEMICAL') return DATASETS.CHEMICALS;
    return undefined;
  };

  const selectedDataset = normalizeDataset(searchItems.length > 0 ? searchItems[0]?.dataset : undefined);
  const constrainedDataset = selectedDataset ?? normalizeDataset(allowedDataset);
  const sections: SectionType[] = noData ? [] : handleSearchItems(searchItems, data);
  const filteredSections = constrainedDataset
    ? sections.filter(section => {
        const title = section.title.toLowerCase();
        if (constrainedDataset === DATASETS.DISEASES) return title.includes('disease');
        if (constrainedDataset === DATASETS.GENES) return title.includes('gene');
        if (constrainedDataset === DATASETS.VARIANTS) return title.includes('variant');
        if (constrainedDataset === DATASETS.CHEMICALS) return title.includes('chemical');
        return true;
      })
    : sections;

  useEffect(() => {
    onDropdownToggle?.(true);
    return () => onDropdownToggle?.(false);
  }, [value, onDropdownToggle]);

  useEffect(() => {
    if (value.trim().length < 3) {
      setData(undefined);
      return;
    }
    setError(null);
    setLoading(true);
    const timeoutId = setTimeout(() => {
      fetchData({
        cancelToken: cancelTokenRef,
        authorization,
        isAuthenticated,
        value,
      })
        .then(setData)
        .catch((err: any) => setError(err?.message ?? 'Error'))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [value, authorization, isAuthenticated]);

  const handleSelectItem = (item: any) => {
    const patched = patchItems(item);
    const patchedDataset = normalizeDataset(patched.dataset);
    if (constrainedDataset && patchedDataset !== constrainedDataset) return;
    setSearchItems(prev =>
      prev.some((p: any) => p.id === patched.id)
        ? prev.filter((p: any) => p.id !== patched.id)
        : [...prev, patched],
    );
  };

  const handleRunSearch = () => {
    const idents = searchItems.length > 0 ? searchItems.map((i: any) => i.id).join('-') : 'ALL';
    const view = searchItems.length > 0 ? searchItems[0].dataset : 'ALL';
    const tab = view === DATASETS.VARIANTS ? 'VDA' : 'GDA';
    const isAcademic = groups?.includes('ROLE_ACADEMIC');
    const source = restrictSources === 'CURATED' || isAcademic ? 'CURATED' : 'ALL';
    navigate({
      pathname: '/search',
      search: `?view=${view}&idents=${idents}&source=${source}&tab=${tab}`,
    });
    setValue('');
  };

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden min-w-0 max-h-[280px] flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0 p-1">
        {loading && <p className="px-3 py-2 text-xs text-gray-500">Loading…</p>}
        {error && (
          <p className="px-3 py-2 text-xs text-red-600">
            {error.includes('403') ? 'Session expired' : error}
          </p>
        )}
        {!loading && !error && noData && value.trim().length >= 3 && (
          <p className="px-3 py-2 text-xs text-gray-500">No results</p>
        )}
        {!loading && !error && !noData && filteredSections.length === 0 && constrainedDataset && (
          <p className="px-3 py-2 text-xs text-gray-500">
            You can only select items of type {String(constrainedDataset).toLowerCase()}.
          </p>
        )}
        {!loading && !error && !noData && filteredSections.length > 0 && (
          <ul className="space-y-0.5 py-1">
            {filteredSections.map(section => (
              <li key={section.title}>
                <p className="px-2 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase text-gray-400 tracking-wider">
                  {section.title}
                </p>
                <ul className="space-y-0">
                  {section.data.map((item: any, index: number) => {
                    const patched = patchItems(item);
                    const isSelected = searchItems.some((p: any) => p.id === patched.id);
                    return (
                      <li key={patched.id ?? index}>
                        <button
                          type="button"
                          onClick={() => handleSelectItem(item)}
                          className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 truncate block ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-800'}`}
                          title={getItemLabel(item)}
                        >
                          <span className="truncate block">{getItemLabel(item)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
      {searchItems.length > 0 && (
        <div className="flex-shrink-0 p-2 border-t border-gray-100">
          <button
            type="button"
            onClick={handleRunSearch}
            className="w-full py-1.5 text-xs font-medium rounded bg-blue text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            Search with selected
          </button>
        </div>
      )}
    </div>
  );
}
